import { Head } from '@inertiajs/react'
import { useState } from 'react'
import { SiteLayout } from '@/components/site-layout'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'

type Props = {
  submission_window: string
}

function loadImages(glob: Record<string, unknown>) {
  return Object.values(glob).filter((src) => typeof src === 'string') as string[]
}

const giamKhaoImages = loadImages(
  import.meta.glob('@/assets/council/ban-giam-khao/*.{png,jpg,jpeg,webp}', {
    eager: true,
    import: 'default',
  })
)
const coVanImages = loadImages(
  import.meta.glob('@/assets/council/ban-co-van/*.{png,jpg,jpeg,webp}', {
    eager: true,
    import: 'default',
  })
)

function splitCoverAndGallery(images: string[]) {
  const cover = images.find((src) => src.includes('cover'))
  const gallery = images.filter((src) => !src.includes('cover')).sort((a, b) => a.localeCompare(b))
  return { cover, gallery }
}

const giamKhao = splitCoverAndGallery(giamKhaoImages)
const coVan = splitCoverAndGallery(coVanImages)

const allImages = [
  ...(giamKhao.cover ? [giamKhao.cover] : []),
  ...giamKhao.gallery,
  ...(coVan.cover ? [coVan.cover] : []),
  ...coVan.gallery,
]

export default function CouncilIndex(_props: Props) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const currentIndex = selectedImage ? allImages.indexOf(selectedImage) : -1

  const showPrev = () => {
    if (currentIndex === -1) return
    const nextIndex = (currentIndex - 1 + allImages.length) % allImages.length
    setSelectedImage(allImages[nextIndex])
  }

  const showNext = () => {
    if (currentIndex === -1) return
    const nextIndex = (currentIndex + 1) % allImages.length
    setSelectedImage(allImages[nextIndex])
  }

  return (
    <SiteLayout>
      <Head title="Hội đồng | Vietnam Pickleball Awards 2025">
        {allImages.map((src) => (
          <link key={src} rel="preload" as="image" href={src} />
        ))}
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
        <CouncilSection
          title="Hội đồng Ban Giám Khảo"
          cover={giamKhao.cover}
          gallery={giamKhao.gallery}
          onSelect={setSelectedImage}
        />

        <CouncilSection
          title="Hội đồng Ban Cố Vấn Chuyên Môn Giải Thưởng"
          cover={coVan.cover}
          gallery={coVan.gallery}
          onSelect={setSelectedImage}
          className="mt-12 sm:mt-16"
        />
      </div>

      <Dialog open={!!selectedImage} onOpenChange={(open) => !open && setSelectedImage(null)}>
        <DialogContent className="flex w-auto max-w-[94vw] items-center justify-center overflow-hidden rounded-2xl bg-white/95 p-2 shadow-[0_30px_90px_-40px_rgba(4,33,51,0.45)] sm:rounded-3xl sm:p-6">
          <DialogTitle className="sr-only">Ảnh hội đồng</DialogTitle>
          {selectedImage && (
            <div className="relative flex items-center justify-center">
              <img
                src={selectedImage}
                alt="Ảnh hội đồng"
                className="max-h-[85vh] w-auto max-w-full rounded-lg object-contain"
              />
              {allImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={showPrev}
                    aria-label="Ảnh trước"
                    className="absolute left-2 flex h-9 w-9 items-center justify-center rounded-full border border-[#042133]/20 bg-white/90 text-[#042133] text-base shadow-sm transition hover:bg-white sm:h-10 sm:w-10 sm:text-lg"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    onClick={showNext}
                    aria-label="Ảnh tiếp theo"
                    className="absolute right-2 flex h-9 w-9 items-center justify-center rounded-full border border-[#042133]/20 bg-white/90 text-[#042133] text-base shadow-sm transition hover:bg-white sm:h-10 sm:w-10 sm:text-lg"
                  >
                    ›
                  </button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SiteLayout>
  )
}

function CouncilSection({
  title,
  cover,
  gallery,
  onSelect,
  className,
}: {
  title: string
  cover: string | undefined
  gallery: string[]
  onSelect: (src: string) => void
  className?: string
}) {
  return (
    <section className={className}>
      <h2 className="text-center font-semibold text-[#5b7788] text-[10px] uppercase tracking-[0.25em] sm:text-xs">
        {title}
      </h2>
      {cover && (
        <button
          type="button"
          onClick={() => onSelect(cover)}
          className="mt-4 w-full overflow-hidden rounded-2xl border border-[#042133]/10 bg-white/80 shadow-[0_24px_70px_-50px_rgba(4,33,51,0.4)] sm:mt-6"
        >
          <img src={cover} alt={title} className="h-auto w-full" />
        </button>
      )}
      {gallery.length > 0 && (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:mt-8 sm:grid-cols-3 lg:grid-cols-4">
          {gallery.map((src, index) => (
            <button
              key={src}
              type="button"
              onClick={() => onSelect(src)}
              className="aspect-square overflow-hidden rounded-2xl border border-[#042133]/10 bg-linear-to-br from-[#f7fbff] via-[#eaf1f7] to-[#dfe9f0] p-2.5 shadow-[0_20px_60px_-45px_rgba(4,33,51,0.45)]"
            >
              <div className="h-full w-full overflow-hidden rounded-xl">
                <img
                  src={src}
                  alt={`${title} ${index + 1}`}
                  className="h-full w-full object-contain"
                />
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
